import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Gear, UsersThree, ShieldCheck } from '@phosphor-icons/react';
import { libraryImages } from '../assets/imageLibrary';

// Hero visual — the flagship Saudi Montessori classroom moment. Sourced from
// the original assets library.
const heroImage = libraryImages.saudiClassroomHero;

const pillars = [
  {
    icon: Target,
    title: 'استشارات',
    desc: 'حلول استشارية مرتكزة على الخبرة والأثر',
  },
  {
    icon: Gear,
    title: 'تطوير',
    desc: 'تطوير البرامج والعمليات وفق أفضل المعايير',
  },
  {
    icon: UsersThree,
    title: 'تمكين',
    desc: 'تمكين الكوادر والمؤسسات لتحقيق الجودة المستدامة',
  },
  {
    icon: ShieldCheck,
    title: 'استدامة',
    desc: 'لتأسيس بيئات آمنة ومحفزة تنمو فيها الطفولة',
  },
];

const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      dir="rtl"
      className="relative bg-[#F7F5F2] overflow-hidden isolate pt-[150px] md:pt-[168px] pb-14 md:pb-20"
    >
      {/* LEFT-side classroom image — softly blended into cream on its inner edge */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[52%] md:w-[48%] lg:w-[46%] -z-10 pointer-events-none"
        aria-hidden="true"
      >
        <img
          src={heroImage}
          alt=""
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover object-center"
        />
        {/* Blurred/dreamy blend on the inner (right) edge → seamlessly merges into the cream canvas */}
        <div className="absolute inset-y-0 right-0 w-[72%] bg-gradient-to-l from-[#F7F5F2] via-[#F7F5F2]/90 via-25% to-transparent" />
        {/* Full soft overlay to reduce sharpness and give the calm/dreamy feel */}
        <div className="absolute inset-0 bg-[#F7F5F2]/30" />
        {/* Top and bottom vignettes */}
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#F7F5F2]/80 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#F7F5F2]/85 to-transparent" />
      </div>

      {/* Content column — anchored to the RIGHT (RTL start) so it sits opposite the image */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-[600px] lg:max-w-[660px] text-center"
        >
          {/* Institutional micro-tag with dash */}
          <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
            <span className="text-[11px] sm:text-[12px] font-medium tracking-[0.24em] text-[#4A5D23] uppercase font-['Noto_Kufi_Arabic']">
              منصة استشارية ومهنية متخصصة
            </span>
            <span className="w-10 h-px bg-[#4A5D23]" aria-hidden="true" />
          </div>

          {/* Main serif headline */}
          <h1
            data-testid="hero-title"
            className="font-['Playfair_Display'] font-semibold text-[#1F3D2B] leading-[0.98] tracking-[-0.025em] text-[52px] sm:text-[78px] md:text-[96px] lg:text-[116px] xl:text-[128px]"
            style={{ fontFeatureSettings: '"liga","kern","calt","dlig"' }}
          >
            Childhood
            <br />
            Horizons
          </h1>

          {/* Gold divider with dot */}
          <div
            className="flex items-center justify-center gap-3 mt-8 sm:mt-10 mb-5 sm:mb-6"
            aria-hidden="true"
          >
            <span className="h-px w-16 bg-[#C9A96E]" />
            <span className="h-[6px] w-[6px] rounded-full bg-[#C9A96E]" />
            <span className="h-px w-16 bg-[#C9A96E]" />
          </div>

          {/* Arabic subtitle */}
          <p
            data-testid="hero-subtitle-ar"
            className="font-['Noto_Kufi_Arabic'] font-semibold text-[#2C3814] text-[22px] sm:text-[26px] md:text-[30px] leading-[1.5] tracking-[-0.005em]"
          >
            خبرات متخصصة للطفولة المبكرة
          </p>

          {/* Bilingual tagline — subtle, small */}
          <div data-testid="hero-tagline" className="mt-4 leading-tight">
            <p className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-[16px] sm:text-[19px] tracking-[-0.01em]">
              نُجسِّد الأثر
            </p>
            <p className="mt-1 font-['Playfair_Display'] italic text-[#7A866E] text-[12px] sm:text-[14px] tracking-[0.02em]">
              Show, Don&apos;t Tell
            </p>
          </div>

          {/* Description paragraph */}
          <p className="mt-6 sm:mt-7 text-[15px] sm:text-[17px] md:text-[18px] text-[#3D512D] leading-[2] font-medium max-w-[560px] mx-auto font-['Noto_Kufi_Arabic']">
            نقدم استشارات وحلولاً نوعية لتطوير بيئات وبرامج الطفولة المبكرة،
            بمعايير عالمية وأثر ملموس.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row-reverse justify-center gap-3 sm:gap-4 mt-8 sm:mt-10">
            <Link to="/#services" className="w-full sm:w-auto">
              <button
                data-testid="hero-cta-primary"
                className="w-full sm:w-auto bg-[#1F3D2B] text-white px-9 py-4 rounded-2xl hover:bg-[#163020] transition-all duration-300 text-sm sm:text-base font-semibold tracking-tight shadow-[0_10px_30px_-12px_rgba(31,61,43,0.5)] font-['Noto_Kufi_Arabic']"
              >
                استكشف خدماتنا
              </button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <button
                data-testid="hero-cta-secondary"
                className="w-full sm:w-auto border border-[#1F3D2B] text-[#1F3D2B] bg-white/50 backdrop-blur-sm px-9 py-4 rounded-2xl hover:bg-[#1F3D2B] hover:text-white transition-all duration-300 text-sm sm:text-base font-semibold tracking-tight font-['Noto_Kufi_Arabic']"
              >
                تواصل معنا
              </button>
            </Link>
          </div>
        </motion.div>

        {/* Four pillars card — anchored at the bottom of the hero, spanning main content width */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-pillars"
          className="relative mt-12 sm:mt-16 md:mt-20 bg-white/85 backdrop-blur-md rounded-[28px] ring-1 ring-[#E6E0D6] shadow-[0_28px_60px_-30px_rgba(31,61,43,0.28)] px-6 sm:px-8 py-8 sm:py-10"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x md:divide-x-reverse divide-[#E6E0D6]">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`pillar-${p.title}`}
                  className="flex flex-col items-center text-center px-2 sm:px-4 py-5 md:py-2"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center mb-3 text-[#4A5D23]">
                    <Icon size={38} weight="regular" />
                  </div>
                  <h3 className="font-['Noto_Kufi_Arabic'] font-bold text-[#1F3D2B] text-[16px] sm:text-[18px] tracking-[0.02em]">
                    {p.title}
                  </h3>
                  <p className="mt-2 font-['Noto_Kufi_Arabic'] text-[12px] sm:text-[13px] text-[#7A866E] leading-[1.7] max-w-[180px]">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
